import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createEmailSnippet,
  deleteEmailSnippet,
  listEmailSnippets,
  updateEmailSnippet,
} from "@/api/email-templates";
import {
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsTable,
  SettingsTextarea,
} from "@/components/settings/settings-ui";

export function SettingsSnippetsPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["email-snippets"],
    queryFn: listEmailSnippets,
  });

  const saveMut = useMutation({
    mutationFn: () =>
      editingId
        ? updateEmailSnippet(editingId, { name, bodyText })
        : createEmailSnippet({ name, bodyText }),
    onSuccess: () => {
      setName("");
      setBodyText("");
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["email-snippets"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteEmailSnippet,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-snippets"] }),
  });

  const snippets = listQuery.data?.snippets ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Snippets"
        description="Reusable text blocks for compose and reply in Emails (and sequence steps)."
      />
      <SettingsSection title="Snippets">
        <SettingsTable
          columns={["Name", "Preview", "Actions"]}
          rows={snippets.map((s) => ({
            Name: s.name,
            Preview: s.bodyText.slice(0, 80),
            Actions: (
              <span className="space-x-2">
                <button
                  type="button"
                  className="text-brand underline"
                  onClick={() => {
                    setEditingId(s.snippetId);
                    setName(s.name);
                    setBodyText(s.bodyText);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-red-600 underline"
                  onClick={() => deleteMut.mutate(s.snippetId)}
                >
                  Delete
                </button>
              </span>
            ),
          }))}
          empty="No snippets yet."
        />
      </SettingsSection>
      <SettingsSection title={editingId ? "Edit snippet" : "New snippet"}>
        <div className="grid max-w-lg gap-2">
          <SettingsInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
          />
          <SettingsTextarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={5}
            placeholder="Snippet body…"
          />
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white disabled:opacity-50"
            disabled={!name.trim() || !bodyText.trim()}
            onClick={() => saveMut.mutate()}
          >
            Save
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}
